import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PQRFormDialog } from "@/features/pqr";
import {
  AuthActionError,
  authErrorToTranslationKey,
  recoverPendingDeletionAccount,
  signInWithPassword,
} from "@/features/auth/api/authClient";
import { getBanNoticeTranslationInput } from "@/features/auth/banNotice";
import { type LoginFormValues, loginSchema } from "@/features/auth/validation/authSchemas";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPqrDialogOpen, setIsPqrDialogOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [canRecoverAccount, setCanRecoverAccount] = useState(false);
  const [recoverMessage, setRecoverMessage] = useState<string | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: LoginFormValues) {
    setSubmitError(null);
    setCanRecoverAccount(false);
    setRecoverMessage(null);

    try {
      await signInWithPassword(values);
      navigate("/app", { replace: true });
    } catch (error) {
      if (error instanceof AuthActionError) {
        if (error.code === "verification-required") {
          navigate("/verify-email-pending", {
            replace: true,
            state: { email: values.email },
          });
          return;
        }

        if (error.code === "account-banned") {
          const translation = getBanNoticeTranslationInput(error.details?.banNotice ?? null);
          setSubmitError(t(translation.key, translation.values));
          return;
        }

        if (error.code === "account-pending-deletion") {
          setCanRecoverAccount(true);
          setSubmitError(t(authErrorToTranslationKey(error.code), {
            date: error.details?.deletionNotice?.deletion_scheduled_at
              ? new Intl.DateTimeFormat(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              }).format(new Date(error.details.deletionNotice.deletion_scheduled_at))
              : undefined,
          }));
          return;
        }

        setSubmitError(t(authErrorToTranslationKey(error.code)));
        return;
      }

      setSubmitError(t("auth.errors.generic"));
    }
  }

  async function handleRecoverAccount() {
    setSubmitError(null);
    setRecoverMessage(null);

    const values = form.getValues();
    try {
      await recoverPendingDeletionAccount(values);
      setCanRecoverAccount(false);
      setRecoverMessage(t("auth.login.recoverSuccess"));
    } catch {
      setSubmitError(t("auth.errors.recoverFailed"));
    }
  }

  return (
    <>
      <AuthLayout
        eyebrow={t("auth.meta.eyebrow")}
        title={t("auth.login.title")}
        subtitle={t("auth.login.subtitle")}
        footer={
          <div className="space-y-4">
            <p className="text-sm text-ink/60">
              {t("auth.actions.needAccount")}{" "}
              <Link className="font-semibold text-ink" to="/register">
                {t("auth.register.submit")}
              </Link>
            </p>
            <div className="rounded-[1.5rem] border border-ink/10 bg-white/55 px-4 py-4">
              <p className="text-sm text-ink/65">{t("pqr.login.help")}</p>
              <Button
                className="mt-3"
                type="button"
                variant="outline"
                onClick={() => setIsPqrDialogOpen(true)}
              >
                {t("pqr.actions.contactUs")}
              </Button>
            </div>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div>
            <Label htmlFor="login-email">{t("auth.fields.email")}</Label>
            <Input id="login-email" type="email" autoComplete="email" {...form.register("email")} />
            {form.formState.errors.email ? (
              <FormMessage>{t(form.formState.errors.email.message ?? "auth.errors.generic")}</FormMessage>
            ) : null}
          </div>

          <div>
            <Label htmlFor="login-password">{t("auth.fields.password")}</Label>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              {...form.register("password")}
            />
            {form.formState.errors.password ? (
              <FormMessage>{t(form.formState.errors.password.message ?? "auth.errors.generic")}</FormMessage>
            ) : null}
          </div>

          <div className="flex items-center justify-end">
            <Link className="text-sm font-semibold text-ink/70 hover:text-ink" to="/forgot-password">
              {t("auth.login.forgot")}
            </Link>
          </div>

          {submitError ? <FormMessage>{submitError}</FormMessage> : null}
          {recoverMessage ? <FormMessage className="text-olive">{recoverMessage}</FormMessage> : null}

          <Button className="w-full" type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? t("common.saving") : t("auth.login.submit")}
          </Button>
          {canRecoverAccount ? (
            <Button
              className="w-full"
              disabled={form.formState.isSubmitting}
              type="button"
              variant="outline"
              onClick={handleRecoverAccount}
            >
              {t("auth.login.recoverAccount")}
            </Button>
          ) : null}
        </form>
      </AuthLayout>
      <PQRFormDialog
        isAuthenticated={false}
        onOpenChange={setIsPqrDialogOpen}
        open={isPqrDialogOpen}
      />
    </>
  );
}
